'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Search, X } from 'lucide-react';
import { fetchResultadosPublicos, type ResultadoPublicoApi } from '@/services/indicadores';
import { fetchCategorias, type CategoriaApi } from '@/services/catalogos';

const CATEGORIES_META: Record<string, { topColor: string; pillBg: string; pillText: string }> = {
  'Economía': { topColor: 'bg-[#3B82F6]', pillBg: 'bg-[#EFF6FF]', pillText: 'text-[#1D4ED8]' },
  'Educación': { topColor: 'bg-[#F59E0B]', pillBg: 'bg-[#FFFBEB]', pillText: 'text-[#B45309]' },
  'Energía': { topColor: 'bg-[#EAB308]', pillBg: 'bg-[#FEF9C3]', pillText: 'text-[#A16207]' },
  'Ambiente y cambio climático': { topColor: 'bg-[#10B981]', pillBg: 'bg-[#ECFDF5]', pillText: 'text-[#047857]' },
  'Finanzas': { topColor: 'bg-[#059669]', pillBg: 'bg-[#D1FAE5]', pillText: 'text-[#065F46]' },
  'Gobernanza': { topColor: 'bg-[#64748B]', pillBg: 'bg-[#F8FAFC]', pillText: 'text-[#334155]' },
  'Salud': { topColor: 'bg-[#F43F5E]', pillBg: 'bg-[#FFF1F2]', pillText: 'text-[#BE123C]' },
  'Viviendas': { topColor: 'bg-[#8B5CF6]', pillBg: 'bg-[#F5F3FF]', pillText: 'text-[#6D28D9]' },
  'Población y condiciones sociales': { topColor: 'bg-[#EC4899]', pillBg: 'bg-[#FDF2F8]', pillText: 'text-[#BE185D]' },
  'Recreación': { topColor: 'bg-[#F97316]', pillBg: 'bg-[#FFF7ED]', pillText: 'text-[#C2410C]' },
  'Seguridad': { topColor: 'bg-[#EF4444]', pillBg: 'bg-[#FEF2F2]', pillText: 'text-[#B91C1C]' },
  'Residuos sólidos': { topColor: 'bg-[#78716C]', pillBg: 'bg-[#F5F5F4]', pillText: 'text-[#44403C]' },
  'Deporte y cultura': { topColor: 'bg-[#D946EF]', pillBg: 'bg-[#FDF4FF]', pillText: 'text-[#A21CAF]' },
  'Telecomunicaciones': { topColor: 'bg-[#0EA5E9]', pillBg: 'bg-[#F0F9FF]', pillText: 'text-[#0369A1]' },
  'Transporte': { topColor: 'bg-[#6366F1]', pillBg: 'bg-[#EEF2FF]', pillText: 'text-[#4338CA]' },
  'Agricultura urbana y/o local y seguridad alimentaria': { topColor: 'bg-[#84CC16]', pillBg: 'bg-[#ECFCCB]', pillText: 'text-[#4D7C0F]' },
  'Planificación urbana': { topColor: 'bg-[#14B8A6]', pillBg: 'bg-[#F0FDFA]', pillText: 'text-[#0F766E]' },
  'Aguas residuales': { topColor: 'bg-[#94A3B8]', pillBg: 'bg-[#F8FAFC]', pillText: 'text-[#475569]' },
  'Agua': { topColor: 'bg-[#06B6D4]', pillBg: 'bg-[#ECFEFF]', pillText: 'text-[#0E7490]' },
};

const ALL_CATEGORIES = [
  'Economía', 'Educación', 'Energía', 'Ambiente y cambio climático', 'Finanzas',
  'Gobernanza', 'Salud', 'Viviendas', 'Población y condiciones sociales', 'Recreación',
  'Seguridad', 'Residuos sólidos', 'Deporte y cultura', 'Telecomunicaciones', 'Transporte',
  'Agricultura urbana y/o local y seguridad alimentaria', 'Planificación urbana',
  'Aguas residuales', 'Agua'
];

const CARD_PAGE_SIZE_OPTIONS = [8, 12, 16, 24, 48];

export default function PublicPortalPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [indicators, setIndicators] = useState<ResultadoPublicoApi[]>([]);
  const [categories, setCategories] = useState<CategoriaApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(12);

  useEffect(() => {
    let active = true;
    const loadData = async () => {
      try {
        const [cats, res] = await Promise.all([
          fetchCategorias(),
          fetchResultadosPublicos(),
        ]);
        if (active) {
          if (cats.length > 0) setCategories(cats);
          setIndicators(res);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load dynamic data', err);
        if (active) setLoading(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const displayIndicators = useMemo(
    () =>
      indicators.map((ind, idx) => {
        const yearStr = ind.periodo_referencia
          ? new Date(ind.periodo_referencia).getFullYear().toString()
          : 'S/D';
        return {
          rowKey: `${ind.corrida_id}-${ind.codigo_interno || idx}`,
          id: ind.codigo_interno || String(ind.corrida_id),
          title: ind.indicador,
          category: ind.categoria,
          periodYear: yearStr,
          value:
            typeof ind.valor_resultado === 'number'
              ? ind.valor_resultado.toLocaleString('es-AR')
              : ind.valor_resultado,
          unit: `${ind.unidad_simbolo || ind.unidad_medida} · ${yearStr}`,
        };
      }),
    [indicators]
  );

  const latestPeriodLabel = useMemo(() => {
    const years = indicators
      .map((ind) => (ind.periodo_referencia ? new Date(ind.periodo_referencia).getFullYear() : 0))
      .filter((y) => y > 1900);
    if (years.length === 0) return 'Sin corridas publicadas';
    return `Período ${Math.max(...years)}`;
  }, [indicators]);

  const displayCategories = useMemo(() => {
    if (categories.length > 0) {
      return categories.map((c) => c.nombre);
    }
    return ALL_CATEGORIES;
  }, [categories]);

  const filteredIndicators = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return displayIndicators.filter((ind) => {
      const matchesCategory = activeCategory ? ind.category === activeCategory : true;
      const matchesSearch =
        q === '' ||
        ind.title.toLowerCase().includes(q) ||
        ind.id.toLowerCase().includes(q) ||
        ind.category.toLowerCase().includes(q);

      return matchesCategory && matchesSearch;
    });
  }, [displayIndicators, activeCategory, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeCategory, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredIndicators.length / itemsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedIndicators = useMemo(() => {
    const start = (safeCurrentPage - 1) * itemsPerPage;
    return filteredIndicators.slice(start, start + itemsPerPage);
  }, [filteredIndicators, safeCurrentPage, itemsPerPage]);

  const toggleCategory = (cat: string) => {
    setActiveCategory((prev) => (prev === cat ? null : cat));
  };

  return (
    <>
      {/* 2. Hero Section */}
      <section className="bg-[#003876] text-white px-8 py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#002B5B] rounded-full translate-x-1/3 -translate-y-1/4 pointer-events-none"></div>

        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-5xl font-[family-name:var(--font-righteous)] mb-4 leading-tight tracking-wide">
            Sistema de Indicadores de Ciudad Inteligente y Sostenible
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-2 font-light">
            Municipio de Luján · Acceso público y abierto a los indicadores estratégicos de la ciudad
          </p>
          <p className="text-sm text-[#FDE047] font-medium tracking-wide mb-10">
            Alineado con ISO 37120 / IRAM-ISO 37120 · Alianza Universitaria Argentina-Europea
          </p>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row mb-8 max-w-3xl">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar indicador por nombre, código o tema..."
                className="w-full bg-white pl-12 pr-10 py-4 rounded-l-xl sm:rounded-r-none rounded-r-xl text-gray-900 focus:outline-none text-lg shadow-lg"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            <button className="bg-[#FDE047] hover:bg-[#FACC15] text-[#0B2A56] font-bold text-lg px-10 py-4 sm:rounded-l-none rounded-r-xl sm:rounded-r-xl rounded-l-xl mt-2 sm:mt-0 transition-all shadow-lg active:scale-95">
              Buscar
            </button>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-white/70 mr-2">Explorar por tema:</span>
            {displayCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`px-5 py-2 rounded-full text-sm transition-all border ${
                  activeCategory === cat
                    ? 'bg-[#FDE047] text-[#0B2A56] font-bold border-[#FDE047] shadow-md'
                    : 'bg-[#1A4B8C] text-white border-transparent hover:bg-[#2563EB]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Indicadores Vigentes Section */}
      <section className="bg-white px-8 py-16 flex-1">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-3xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-2 inline-block border-b-4 border-[#3B82F6] pb-1">
                Indicadores Vigentes
              </h3>
              <p className="text-gray-500 text-sm mt-2">
                Última corrida aprobada: {latestPeriodLabel} · {filteredIndicators.length} indicador(es) encontrado(s)
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              {/* Show active filters summary */}
              {(activeCategory || searchTerm) && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Filtros activos:</span>
                  {activeCategory && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-md flex items-center gap-1">
                      {activeCategory}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setActiveCategory(null)} />
                    </span>
                  )}
                  {searchTerm && (
                    <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-md flex items-center gap-1">
                      &quot;{searchTerm}&quot;
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchTerm('')} />
                    </span>
                  )}
                </div>
              )}

              <label className="flex items-center gap-2 text-xs font-semibold text-gray-500">
                <span>Por página:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-bold text-[#002B5B] outline-none focus:border-[#002B5B]"
                >
                  {CARD_PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse flex flex-col h-64">
                  <div className="w-20 h-5 rounded-full bg-gray-200 mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-6"></div>
                  <div className="h-10 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3 mt-auto"></div>
                </div>
              ))}
            </div>
          ) : filteredIndicators.length === 0 ? (
            <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h4 className="text-xl font-bold text-gray-500 mb-2">No se encontraron indicadores</h4>
              <p className="text-gray-400">Intenta probar con otros términos de búsqueda o selecciona otro tema.</p>
              {(searchTerm || activeCategory) && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory(null);
                  }}
                  className="mt-6 text-blue-600 font-bold hover:underline"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {paginatedIndicators.map((ind) => {
                  const meta = CATEGORIES_META[ind.category] || CATEGORIES_META['Gobernanza'];

                  return (
                    <div
                      key={ind.rowKey}
                      className="bg-white rounded-2xl shadow-[0_4px_20px_rgb(0,0,0,0.06)] border border-gray-100 p-6 relative overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.1)] transition-all flex flex-col group"
                    >
                      <div className={`absolute top-0 left-0 right-0 h-1.5 ${meta.topColor}`}></div>
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 self-start ${meta.pillBg} ${meta.pillText}`}
                      >
                        {ind.category}
                      </span>
                      <h4 className="font-bold text-gray-800 text-lg mb-6 leading-tight min-h-[48px]">
                        {ind.title}
                      </h4>
                      <div className="mb-6 flex-1">
                        <div className="text-4xl font-[family-name:var(--font-righteous)] text-[#003876] tracking-wide mb-1">
                          {ind.value}
                        </div>
                        <div className="text-xs text-gray-400 font-medium">{ind.unit}</div>
                      </div>
                      <Link
                        href={`/indicadores/${ind.id}`}
                        className="bg-[#F8FAFC] hover:bg-[#E2E8F0] text-[#0F172A] text-sm font-bold py-2 px-4 rounded-lg w-full text-left transition-colors flex items-center gap-2"
                      >
                        Ver detalle{' '}
                        <span className="opacity-60 group-hover:translate-x-1 transition-transform">
                          →
                        </span>
                      </Link>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-100 pt-6">
                  <div className="text-xs text-gray-500">
                    Mostrando{' '}
                    <span className="font-bold text-gray-800">
                      {(safeCurrentPage - 1) * itemsPerPage + 1}
                    </span>{' '}
                    a{' '}
                    <span className="font-bold text-gray-800">
                      {Math.min(safeCurrentPage * itemsPerPage, filteredIndicators.length)}
                    </span>{' '}
                    de <span className="font-bold text-gray-800">{filteredIndicators.length}</span>{' '}
                    indicadores
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={safeCurrentPage === 1}
                      className="rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        type="button"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`min-w-8 rounded-lg px-3 py-2 text-xs font-bold transition ${
                          safeCurrentPage === pageNum
                            ? 'bg-[#003876] text-white'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safeCurrentPage === totalPages}
                      className="rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* 4. Data Access Section */}
      <section className="bg-[#F3F4F6] px-8 py-16">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-2xl font-[family-name:var(--font-righteous)] text-[#002B5B] mb-2">
              Acceso Abierto a los Datos
            </h3>
            <p className="text-gray-500">
              Descargá los datasets completos en formatos abiertos (CSV / PDF)
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a
              href="/api/externo/resultados-publicos?format=csv"
              download="resultados-publicos.csv"
              className="bg-[#0052A3] hover:bg-[#003876] text-white font-bold py-3 px-8 rounded-lg shadow transition-colors inline-block text-center"
            >
              Descargar CSV
            </a>
            <button
              onClick={() => window.print()}
              className="bg-white hover:bg-gray-50 text-[#0052A3] font-bold py-3 px-8 rounded-lg shadow-sm border border-gray-200 transition-colors"
            >
              Descargar PDF
            </button>
            <span className="text-xs text-gray-400 font-medium ml-2 hidden lg:inline-block">
              Sin autenticación requerida · Acceso 100% público
            </span>
          </div>
        </div>
      </section>
    </>
  );
}
